import { useCallback } from 'react'
import { isUndefined } from 'lodash'

import {
  QueueStatsFilterRequest,
  RangeDirection,
  TimeRange,
  TimeScale,
  VolumeComparisonType,
} from 'modules/Monitoring/types'

import { calculateInitialTimeRangeForFilteredTimeframe } from '../utils/calculateInitialTimeRangeForFilteredTimeframe'
import { calculateInitialTimeRangePages } from '../utils/calculateInitialTimeRangePages'
import { calculateNextTimeRange } from '../utils/calculateNextTimeRange'
import { checkFutureDateRangeSupported } from '../utils/checkFutureDateRangeSupported'

export type TimeRangeSearchParams = {
  timeRangePages: TimeRange[]
  currentTimeRangeIndex: number
}
export type CalculateTimeRangeParamsType = (args: {
  timeScale: TimeScale
  supportsFuture: boolean
  forceSetFutureRange?: boolean
  timeframeFilter?: Pick<QueueStatsFilterRequest, 'from' | 'to'>
}) => TimeRangeSearchParams

export const useTimeRangeData = ({
  timeRangePages,
  currentTimeRangeIndex,
}: TimeRangeSearchParams) => {
  const currentTimeRange = timeRangePages[currentTimeRangeIndex]
  const hasNext = Boolean(timeRangePages[currentTimeRangeIndex + 1])

  const getAdjustedTimeRangeWithFiltersTimeframe = useCallback(
    ({
      timeScale,
      initialTimeRanges,
      timeframeFilter,
    }: {
      timeScale: TimeScale
      initialTimeRanges: TimeRange[]
      timeframeFilter?: Pick<QueueStatsFilterRequest, 'from' | 'to'>
    }): TimeRangeSearchParams | null => {
      if (!timeframeFilter?.to || !timeframeFilter.from) {
        return null
      }

      const timeRangeResult = calculateInitialTimeRangeForFilteredTimeframe({
        ranges: initialTimeRanges,
        currentTimeRangeIndex: 0,
        timeScale,
        timeframeFilters: {
          from: timeframeFilter.from,
          to: timeframeFilter.to,
        },
      })

      if (!timeRangeResult) {
        return null
      }

      return {
        timeRangePages: timeRangeResult.ranges,
        currentTimeRangeIndex: timeRangeResult.rangeIndex,
      }
    },
    [],
  )

  const calculateTimeRangeParams: CalculateTimeRangeParamsType = useCallback(
    ({ timeScale, supportsFuture, forceSetFutureRange = false, timeframeFilter }) => {
      const initialTimeRangePages = calculateInitialTimeRangePages({
        timeScale,
        supportsFuture,
      })
      const lastPageIndex = initialTimeRangePages.length - 1

      const isFutureShown =
        forceSetFutureRange || (supportsFuture && currentTimeRangeIndex === lastPageIndex)
      const baseIndex = isFutureShown ? lastPageIndex : 0

      const adjustedResult = getAdjustedTimeRangeWithFiltersTimeframe({
        initialTimeRanges: initialTimeRangePages,
        timeScale,
        timeframeFilter,
      })

      if (adjustedResult) {
        return adjustedResult
      }

      return {
        timeRangePages: initialTimeRangePages,
        currentTimeRangeIndex: baseIndex,
      }
    },
    [currentTimeRangeIndex, getAdjustedTimeRangeWithFiltersTimeframe],
  )

  const calculateNextRange = useCallback(
    ({
      direction,
      timeScale,
      comparisonType,
    }: {
      direction: RangeDirection
      timeScale: TimeScale
      comparisonType: VolumeComparisonType
    }) => {
      const supportsFuture = checkFutureDateRangeSupported({ timeScale, comparisonType })

      const nextRange = calculateNextTimeRange({
        direction,
        ranges: timeRangePages,
        currentTimeRangeIndex,
        timeScale,
        supportsFuture,
      })

      if (!nextRange) {
        return null
      }

      const result = {
        timeRangePages: nextRange.ranges ?? timeRangePages,
        currentTimeRangeIndex: nextRange.rangeIndex ?? currentTimeRangeIndex,
        isLastPage: false,
      }

      const hasNextPage = result.timeRangePages[result.currentTimeRangeIndex + 1]

      if (isUndefined(hasNextPage)) {
        result.isLastPage = true
      }

      return result
    },
    [timeRangePages, currentTimeRangeIndex],
  )

  return {
    currentTimeRange,
    calculateTimeRangeParams,
    calculateNextRange,
    hasNext,
  }
}
